import { ConfluencePageAri } from '@atlassian/ari';

import type {
	preloadIncomingLinksSampleQuery$data,
	preloadIncomingLinksSampleQuery$variables,
} from './__generated__/preloadIncomingLinksSampleQuery.graphql.ts';

export type IncomingLinksSsrStatus = 'present' | 'empty' | 'unavailable';

export type IncomingLinksSsrState = Readonly<{
	contentId: string;
	status: IncomingLinksSsrStatus;
}>;

export type IncomingLinksSampleUnavailableReason =
	| 'invalid-input'
	| 'malformed-response'
	| 'query-error';

// The preload derives presence from a bounded sample; only the row count leaves this layer, but the
// underlying query (and its cached entry) holds the source ARIs like any other preloaded query.
export type IncomingLinksSampleResult =
	| Readonly<{ status: 'available'; count: number }>
	| Readonly<{ status: 'unavailable'; reason: IncomingLinksSampleUnavailableReason }>;

// The sample is bounded to this many source rows so a hot page never pulls its full incoming set;
// the outcome buckets are `0` / `1-2` / `3+`.
export const INCOMING_LINKS_SAMPLE_LIMIT = 3;

const SOURCE_COLUMN = 'source';

export const createIncomingLinksSsrState = (
	contentId: string,
	status: IncomingLinksSsrStatus,
): IncomingLinksSsrState => Object.freeze({ contentId, status });

const buildIncomingLinksCypher = (targetAri: string): string =>
	[
		`MATCH (source)-[:content_referenced_entity]->(target:ConfluencePage {ari: "${targetAri}"})`,
		'WHERE source <> target',
		'RETURN source',
		`LIMIT ${INCOMING_LINKS_SAMPLE_LIMIT}`,
	].join('\n');

// Shared by the preloader (network) and the cache-only reader so both key the GraphQL cache on a
// byte-identical query and variables. Returns `undefined` for input that must not be queried, which
// the reader treats as "skip" and the preloader treats as "invalid-input".
export const buildIncomingLinksSampleVariables = (
	cloudId: string,
	contentId: string,
): preloadIncomingLinksSampleQuery$variables | undefined => {
	if (!cloudId.trim() || !/^[1-9]\d*$/.test(contentId)) {
		return undefined;
	}

	let targetAri: string;
	try {
		targetAri = ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString();
	} catch {
		return undefined;
	}

	return {
		cypherQuery: buildIncomingLinksCypher(targetAri),
		first: INCOMING_LINKS_SAMPLE_LIMIT,
	};
};

// Pure normalization of the bounded sample. Defensive against a malformed shape because the response
// is an untyped Cypher projection. Only the row count leaves this function; presence is any sampled
// source row.
export const normalizeIncomingLinksSampleData = (
	data: preloadIncomingLinksSampleQuery$data | null | undefined,
): IncomingLinksSampleResult => {
	const edges = data?.graphStore_cypherQueryV2?.edges;
	if (!edges || edges.length > INCOMING_LINKS_SAMPLE_LIMIT) {
		return { status: 'unavailable', reason: 'malformed-response' };
	}

	for (const edge of edges) {
		const columns = edge.node.columns;
		if (columns.length !== 1) {
			return { status: 'unavailable', reason: 'malformed-response' };
		}

		const column = columns[0];
		if (
			column.key !== SOURCE_COLUMN ||
			column.value?.__typename !== 'GraphStoreCypherQueryV2AriNode' ||
			typeof column.value.id !== 'string' ||
			!column.value.id.startsWith('ari:')
		) {
			return { status: 'unavailable', reason: 'malformed-response' };
		}
	}

	return { status: 'available', count: edges.length };
};

// Maps a query response (from the network preload or the hydrated cache) to the terminal SSR state.
// A malformed sample is `unavailable`; otherwise presence is any sampled row.
export const normalizeIncomingLinksSsrState = (
	contentId: string,
	data: preloadIncomingLinksSampleQuery$data | null | undefined,
): IncomingLinksSsrState => {
	const result = normalizeIncomingLinksSampleData(data);
	if (result.status === 'unavailable') {
		return createIncomingLinksSsrState(contentId, 'unavailable');
	}
	return createIncomingLinksSsrState(contentId, result.count > 0 ? 'present' : 'empty');
};
